import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import type React from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import type { TosVersion } from "@shared/schema";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Plus, Send, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export function TosVersionsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    effectiveAt: "",
    label: "",
    changeSummary: "",
    fullText: "",
  });

  const versionsQuery = useQuery<TosVersion[]>({
    queryKey: ["/api/app/admin/tos-versions"],
  });

  const publishMutation = useMutation({
    mutationFn: async (input: typeof form) => {
      // Convert the date input (YYYY-MM-DD) into an ISO timestamp at
      // midnight UTC so the version id matches the same format as the
      // legacy `TOS_LAST_UPDATED_AT` constant.
      const effectiveAtIso = new Date(`${input.effectiveAt}T00:00:00.000Z`).toISOString();
      const res = await apiRequest("POST", "/api/app/admin/tos-versions", {
        effectiveAt: effectiveAtIso,
        label: input.label,
        changeSummary: input.changeSummary,
        fullText: input.fullText,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Terms of Service published",
        description: "Eligible users will now receive the update email.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/tos-versions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tos/latest"] });
      setShowForm(false);
      setForm({ effectiveAt: "", label: "", changeSummary: "", fullText: "" });
    },
    onError: (err) => {
      toast({
        title: "Publish failed",
        description: parseErrorMessage(err, "Could not publish the new version"),
        variant: "destructive",
      });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.effectiveAt || !form.label || !form.changeSummary || !form.fullText) {
      toast({
        title: "Missing fields",
        description: "All fields are required to publish a new version.",
        variant: "destructive",
      });
      return;
    }
    publishMutation.mutate(form);
  }

  const versions = versionsQuery.data ?? [];
  const latest = versions[0] ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-tos-versions-title">Terms of Service Versions</h3>
          <p className="text-sm text-muted-foreground">
            Every published Terms of Service version is stored here. Publishing a new version automatically emails eligible users about the update.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setShowForm((v) => !v)}
          data-testid="button-publish-tos-version"
        >
          {showForm ? <X className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          {showForm ? "Cancel" : "Publish New Version"}
        </Button>
      </div>

      {latest && (
        <Card className="p-4 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40" data-testid="card-tos-current-version">
          <div className="flex items-center gap-2 text-sm text-blue-900 dark:text-blue-200">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-semibold">Currently published:</span>
            <span data-testid="text-tos-current-label">{latest.label}</span>
            <span className="text-blue-700 dark:text-blue-300">
              (effective {new Date(latest.effectiveAt).toLocaleDateString()})
            </span>
          </div>
        </Card>
      )}

      {showForm && (
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-publish-tos-version">
            <div>
              <Label htmlFor="tos-effective-at">Effective Date</Label>
              <Input
                id="tos-effective-at"
                type="date"
                required
                value={form.effectiveAt}
                onChange={(e) => setForm((f) => ({ ...f, effectiveAt: e.target.value }))}
                className="mt-1"
                data-testid="input-tos-effective-at"
              />
              <p className="text-xs text-muted-foreground mt-1">
                When the new version takes effect. Must be later than the most recent version.
              </p>
            </div>
            <div>
              <Label htmlFor="tos-label">Display Label</Label>
              <Input
                id="tos-label"
                placeholder="e.g. April 22, 2026"
                required
                maxLength={100}
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="mt-1"
                data-testid="input-tos-label"
              />
            </div>
            <div>
              <Label htmlFor="tos-change-summary">Change Summary</Label>
              <Textarea
                id="tos-change-summary"
                placeholder="What changed in this update? Shown in the in-app banner and the /terms callout."
                required
                rows={3}
                value={form.changeSummary}
                onChange={(e) => setForm((f) => ({ ...f, changeSummary: e.target.value }))}
                className="mt-1"
                data-testid="input-tos-change-summary"
              />
            </div>
            <div>
              <Label htmlFor="tos-full-text">Full Text</Label>
              <Textarea
                id="tos-full-text"
                placeholder="Full Terms of Service content for this version (kept for historical record)."
                required
                rows={10}
                value={form.fullText}
                onChange={(e) => setForm((f) => ({ ...f, fullText: e.target.value }))}
                className="mt-1 font-mono text-xs"
                data-testid="input-tos-full-text"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                disabled={publishMutation.isPending}
                data-testid="button-submit-tos-version"
              >
                {publishMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Publish & Notify Users
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
                disabled={publishMutation.isPending}
                data-testid="button-cancel-tos-version"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="p-0">
        {versionsQuery.isLoading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : versions.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="text-tos-versions-empty">
            No Terms of Service versions have been published yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Effective</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Change Summary</TableHead>
                <TableHead>Published At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((v) => (
                <TableRow key={v.id} data-testid={`row-tos-version-${v.id}`}>
                  <TableCell className="whitespace-nowrap" data-testid={`text-tos-effective-${v.id}`}>
                    {new Date(v.effectiveAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell data-testid={`text-tos-label-${v.id}`}>{v.label}</TableCell>
                  <TableCell className="max-w-md text-sm text-muted-foreground" data-testid={`text-tos-summary-${v.id}`}>
                    {v.changeSummary}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {new Date(v.createdAt).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
